import { useEffect, useRef } from 'react';
import './css/NeuralNetBackground.css';

type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  hue: number;
  wave: number;
  waveSpeed: number;
  phase: number;
  phaseSpeed: number;
  baseOpacity: number;
};

type Sparkle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  phase: number;
  phaseSpeed: number;
  rot: number;
  rotSpeed: number;
};

const TEAL_HUE_MIN = 168;
const TEAL_HUE_RANGE = 22;

function isCompactViewport() {
  return window.matchMedia('(max-width: 768px)').matches;
}

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function NeuralNetBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const particles: Particle[] = [];
    const sparkles: Sparkle[] = [];
    const mouse = { x: -1000, y: -1000 };
    let width = 0;
    let height = 0;
    let compact = isCompactViewport();
    let frameId = 0;
    let running = true;

    const ATTRACT_RADIUS = 168;
    const ATTRACT_STRENGTH = 0.08;
    const ATTRACT_DEADZONE = 28;
    const MAX_SPEED = 2.6;

    const spawnParticle = (): Particle => ({
      x: Math.random() * width,
      y: Math.random() * height,
      vx: (Math.random() - 0.5) * 0.45,
      vy: (Math.random() - 0.5) * 0.45,
      radius: Math.random() * 1.8 + 0.8,
      hue: TEAL_HUE_MIN + Math.random() * TEAL_HUE_RANGE,
      wave: Math.random() * Math.PI * 2,
      waveSpeed: Math.random() * 0.018 + 0.008,
      phase: Math.random() * Math.PI * 2,
      phaseSpeed: Math.random() * 0.012 + 0.004,
      baseOpacity: Math.random() * 0.35 + 0.25,
    });

    const spawnSparkle = (): Sparkle => ({
      x: Math.random() * width,
      y: Math.random() * height,
      vx: (Math.random() - 0.5) * 0.22,
      vy: (Math.random() - 0.5) * 0.22,
      phase: Math.random() * Math.PI * 2,
      phaseSpeed: Math.random() * 0.015 + 0.008,
      rot: Math.random() * Math.PI,
      rotSpeed: (Math.random() - 0.5) * 0.012,
    });

    const spawnSparkles = () => {
      const count = compact ? 3 : 6;
      sparkles.length = 0;
      for (let i = 0; i < count; i += 1) sparkles.push(spawnSparkle());
    };

    const spawnParticles = () => {
      const density = compact ? 18000 : 11000;
      const count = Math.max(32, Math.min(compact ? 60 : 140, Math.floor((width * height) / density)));
      particles.length = 0;
      for (let i = 0; i < count; i += 1) particles.push(spawnParticle());
      spawnSparkles();
    };

    const resize = () => {
      const parent = canvas.parentElement;
      const rect = parent?.getBoundingClientRect();
      width = Math.max(1, Math.round(rect?.width || canvas.clientWidth || 1));
      height = Math.max(1, Math.round(rect?.height || canvas.clientHeight || 1));
      compact = isCompactViewport();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const nextW = Math.floor(width * dpr);
      const nextH = Math.floor(height * dpr);
      const sizeChanged = canvas.width !== nextW || canvas.height !== nextH;
      if (sizeChanged) {
        canvas.width = nextW;
        canvas.height = nextH;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (particles.length === 0) {
        spawnParticles();
        return;
      }
      particles.forEach((p) => {
        p.x = Math.max(0, Math.min(width, p.x));
        p.y = Math.max(0, Math.min(height, p.y));
      });
      sparkles.forEach((s) => {
        s.x = Math.max(0, Math.min(width, s.x));
        s.y = Math.max(0, Math.min(height, s.y));
      });
    };

    const particleOpacity = (p: Particle) => {
      const fade = 0.5 + 0.5 * Math.sin(p.phase);
      return p.baseOpacity * (0.12 + 0.88 * fade);
    };

    const clampSpeed = (obj: { vx: number; vy: number }, max: number) => {
      const speed = Math.hypot(obj.vx, obj.vy);
      if (speed > max) {
        obj.vx = (obj.vx / speed) * max;
        obj.vy = (obj.vy / speed) * max;
      }
    };

    const bounce = (obj: { x: number; y: number; vx: number; vy: number }) => {
      if (obj.x < 0) {
        obj.x = 0;
        obj.vx = Math.abs(obj.vx);
      } else if (obj.x > width) {
        obj.x = width;
        obj.vx = -Math.abs(obj.vx);
      }
      if (obj.y < 0) {
        obj.y = 0;
        obj.vy = Math.abs(obj.vy);
      } else if (obj.y > height) {
        obj.y = height;
        obj.vy = -Math.abs(obj.vy);
      }
    };

    const updateParticles = () => {
      particles.forEach((p) => {
        p.wave += p.waveSpeed;
        p.phase += p.phaseSpeed;

        if (!compact && mouse.x >= 0 && mouse.y >= 0) {
          const dx = mouse.x - p.x;
          const dy = mouse.y - p.y;
          const dist = Math.hypot(dx, dy);
          if (dist < ATTRACT_RADIUS && dist > 0.001) {
            const falloff = (ATTRACT_RADIUS - dist) / ATTRACT_RADIUS;
            const pull = dist < ATTRACT_DEADZONE ? falloff * 0.18 : falloff;
            p.vx += (dx / dist) * pull * ATTRACT_STRENGTH;
            p.vy += (dy / dist) * pull * ATTRACT_STRENGTH;
          }
        }

        p.vx *= 0.985;
        p.vy *= 0.985;
        clampSpeed(p, MAX_SPEED);

        p.x += p.vx + Math.sin(p.wave) * 0.35;
        p.y += p.vy + Math.cos(p.wave * 0.7) * 0.22;
        bounce(p);
      });

      sparkles.forEach((s) => {
        s.phase += s.phaseSpeed;
        s.rot += s.rotSpeed;
        s.x += s.vx;
        s.y += s.vy;
        bounce(s);
      });
    };

    const drawBackground = (time: number) => {
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, width, height);

      const waves = compact ? 2 : 3;
      ctx.globalAlpha = 0.14;
      for (let i = 0; i < waves; i += 1) {
        ctx.beginPath();
        ctx.moveTo(0, height / 2);
        const step = compact ? 18 : 10;
        for (let x = 0; x <= width; x += step) {
          const y = height / 2 + Math.cos(x * 0.006 + time * (0.00028 + i * 0.00012)) * (28 + i * 16);
          ctx.lineTo(x, y);
        }
        ctx.strokeStyle = `hsla(${172 + i * 8}, 62%, 48%, 0.35)`;
        ctx.lineWidth = 1.2;
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    };

    const drawSparkles = () => {
      sparkles.forEach((s) => {
        const alpha = (0.5 + 0.5 * Math.sin(s.phase)) * 0.45;
        if (alpha < 0.04) return;
        const size = 2.2 + Math.sin(s.phase * 1.4) * 0.8;
        ctx.save();
        ctx.translate(s.x, s.y);
        ctx.rotate(s.rot);
        ctx.fillStyle = `hsla(172, 78%, 68%, ${alpha})`;
        ctx.fillRect(-size / 2, -0.7, size, 1.4);
        ctx.fillRect(-0.7, -size / 2, 1.4, size);
        ctx.restore();
      });
    };

    const drawConnections = () => {
      const maxDist = compact ? 150 : 200;
      const maxDegree = compact ? 3 : 5;
      const edges: { i: number; j: number; dist: number }[] = [];
      for (let i = 0; i < particles.length; i += 1) {
        for (let j = i + 1; j < particles.length; j += 1) {
          const dist = Math.hypot(particles[i].x - particles[j].x, particles[i].y - particles[j].y);
          if (dist < maxDist) edges.push({ i, j, dist });
        }
      }
      edges.sort((a, b) => a.dist - b.dist);

      const degrees = new Uint8Array(particles.length);
      ctx.lineWidth = 1;
      for (let e = 0; e < edges.length; e += 1) {
        const { i, j, dist } = edges[e];
        if (degrees[i] >= maxDegree || degrees[j] >= maxDegree) continue;
        degrees[i] += 1;
        degrees[j] += 1;
        const a = particles[i];
        const b = particles[j];
        const proximity = (maxDist - dist) / maxDist;
        const vis = Math.max(0.28, (particleOpacity(a) + particleOpacity(b)) * 0.5);
        const strength = proximity * vis * 0.62;
        ctx.strokeStyle = `hsla(${(a.hue + b.hue) / 2}, 68%, 58%, ${strength})`;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }
    };

    const drawParticles = () => {
      particles.forEach((p) => {
        const opacity = particleOpacity(p);
        if (opacity < 0.02) return;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        const gradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.radius * 3.2);
        gradient.addColorStop(0, `hsla(${p.hue}, 82%, 68%, ${opacity})`);
        gradient.addColorStop(0.45, `hsla(${p.hue + 8}, 72%, 58%, ${opacity * 0.45})`);
        gradient.addColorStop(1, `hsla(${p.hue + 14}, 60%, 50%, 0)`);
        ctx.fillStyle = gradient;
        ctx.fill();
      });
    };

    const drawFrame = (time: number) => {
      drawBackground(time);
      drawSparkles();
      if (!prefersReducedMotion()) updateParticles();
      drawConnections();
      drawParticles();
    };

    const loop = (time: number) => {
      if (!running) return;
      drawFrame(time);
      if (!prefersReducedMotion()) {
        frameId = requestAnimationFrame(loop);
      }
    };

    const onMouseMove = (event: MouseEvent) => {
      if (compact) return;
      const rect = canvas.getBoundingClientRect();
      if (
        event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom
      ) {
        mouse.x = -1000;
        mouse.y = -1000;
        return;
      }
      mouse.x = event.clientX - rect.left;
      mouse.y = event.clientY - rect.top;
    };

    const onMouseLeave = () => {
      mouse.x = -1000;
      mouse.y = -1000;
    };

    const onVisibility = () => {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(frameId);
        return;
      }
      if (!running) {
        running = true;
        frameId = requestAnimationFrame(loop);
      }
    };

    resize();
    drawFrame(0);

    const handleResize = () => {
      resize();
      if (prefersReducedMotion()) drawFrame(0);
    };

    const layoutFrame = requestAnimationFrame(handleResize);

    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(handleResize) : null;
    observer?.observe(canvas.parentElement || canvas);
    window.addEventListener('resize', handleResize);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseleave', onMouseLeave);
    document.addEventListener('visibilitychange', onVisibility);

    if (!prefersReducedMotion()) {
      frameId = requestAnimationFrame(loop);
    }

    return () => {
      running = false;
      cancelAnimationFrame(frameId);
      cancelAnimationFrame(layoutFrame);
      observer?.disconnect();
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseleave', onMouseLeave);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return <canvas ref={canvasRef} className="neural-net-bg" aria-hidden />;
}
